import {
	ChangeEvent,
	FunctionComponent,
	MouseEvent as ReactMouseEvent,
	useMemo,
	useState,
} from "react";

import { CircularProgress, Link } from "@mui/material";

import { ensureHttpProtocol } from "@/utils/url";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { InputField } from "@/components/InputField/InputField";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";
import { InfoCardPopover, InfoCardPopoverRow } from "@/components/InfoCardPopover";
import { useUpdateContactOrganizationMutation } from "@/pages/Private/redux/contact/contact.api";

import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import { CrmIcon } from "../../List/components/CrmIcon";
import { ContactProspect } from "../schema/contacts";

export const EditableContactCompanyColumn: FunctionComponent<{
	row: ContactProspect;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const [value, setValue] = useState<string | undefined>(row?.organization?.name || "");
	const [updateOrganization, { isLoading }] = useUpdateContactOrganizationMutation();

	const currentCompanyUrl = useMemo(() => {
		const url = row.organization?.websiteUrl ?? row.organization?.primaryDomain ?? undefined;

		return url ? ensureHttpProtocol(url) : undefined;
	}, [row.organization?.websiteUrl, row.organization?.primaryDomain]);

	if (isLoading) {
		return <CircularProgress />;
	}

	const location = [row.organization?.city, row.organization?.state, row.organization?.country]
		.filter(Boolean)
		.join(", ");

	return (
		<div className="flex w-full max-w-[270px] items-center">
			{row.organization?.logoUrl && (
				<HideOnErrorImg
					alt="logo"
					className="max-h-[24px] h-full w-[24px] rounded mr-1"
					referrerPolicy="no-referrer"
					src={row.organization.logoUrl}
				/>
			)}
			<div className="ml-2 w-full flex flex-row justify-between">
				{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
					<div className="min-w-[260px] absolute left-0 top-0 z-10">
						<InputField
							autoFocus
							handleBlur={() => {
								setShowEdit({ id: 0, name: "" });
							}}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue(event.target.value);
							}}
							name={orderTitle}
							value={value || ""}
							onKeyDown={async (e) => {
								if (e.key === "Enter" && value) {
									await updateOrganization({
										id: row.organization?.id ?? row.id,
										...{ name: value },
									}).unwrap();
									setShowEdit({ id: 0, name: "" });
								}
							}}
						/>
					</div>
				) : (
					<Tooltip
						content={
							<InfoCardPopover
								kind="company"
								onCopy={handleCopyClick}
								rows={
									[
										{ label: "Name", value: row.organization?.name },
										{ label: "Location", value: location || null },
										{
											label: "Employees",
											value: row.organization?.estimatedNumEmployees?.toLocaleString() ?? null,
										},
										{
											label: "Company category",
											value: row.organization?.scoreCompanyCategoryResult?.result,
										},
										{
											label: "Company prio",
											value: row.scorePersonaCategoryResult?.companyScore,
										},
										{
											label: "Short description",
											value: row.organization?.shortDescription,
											long: true,
										},
									] satisfies InfoCardPopoverRow[]
								}
							/>
						}
						exitDelay={100}
					>
						<button
							className="cursor-pointer mr-1 min-w-0 flex-1 max-w-[140px] overflow-hidden block text-left"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(row.organization?.name || "");
							}}
						>
							<span
								className="block truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors"
								onContextMenu={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setShowEdit({ id: row.id, name: orderTitle });
								}}
							>
								{row.organization?.name || ""}
							</span>
						</button>
					</Tooltip>
				)}

				<div className="flex items-center">
					{row.organization?.crmOrganizations?.map((crmOrganization) => (
						<Tooltip
							key={crmOrganization.id}
							content={
								<div className="flex flex-col gap-2">
									<span>Account Owner: {crmOrganization.accountOwner}</span>
									<span>Reference ID: {crmOrganization.referenceId}</span>
								</div>
							}
							disabled={!crmOrganization.url}
						>
							{crmOrganization.url ? (
								<Link href={ensureHttpProtocol(crmOrganization.url)} target="_blank">
									<CrmIcon url={crmOrganization.url ?? ""} />
								</Link>
							) : (
								<CrmIcon url={crmOrganization.url ?? ""} />
							)}
						</Tooltip>
					))}

					{row.organization?.linkedinUrl ? (
						<Link href={ensureHttpProtocol(row.organization.linkedinUrl)} target="_blank">
							<LinkedinIcon status="checked" />
						</Link>
					) : (
						row.organization && <LinkedinIcon status="failed" />
					)}

					{currentCompanyUrl ? (
						<Link href={currentCompanyUrl || ""} target="_blank">
							<WebsiteIcon status="checked" />
						</Link>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
